/**
 * Unified regulatory ingredient explorer.
 *
 * Merges every ingredient the platform knows about — IFRA standards, EU
 * Annex II/III rules, EU Annex III fragrance allergens, MoCRA PFAS list,
 * MoCRA talc list — into a single searchable index keyed on CAS plus name.
 *
 * This is the data model the Regulatory Intelligence "Ingredients" tab reads
 * from, so a search for "Limonene", "138-86-3", or "oakmoss" returns a
 * consolidated regulatory fingerprint.
 */

import structuredIfraData from "../data/structured_ifra_data.reconciled.json";
import mocraReference from "../data/mocra_reference.json";

const normalizeText = (value) =>
  String(value || "").toLowerCase().replace(/\s+/g, " ").trim();

const normalizeCas = (value) =>
  normalizeText(value).replace(/\s+/g, "").replace(/^n\/a$/, "");

const splitCasList = (value) =>
  (Array.isArray(value) ? value : [value])
    .flatMap((entry) => String(entry || "").split(/\s*,\s*/))
    .map(normalizeCas)
    .filter(Boolean);

// Upsert helper keyed primarily by the first CAS number, falling back to
// normalised ingredient name when no CAS is available.
const upsert = (index, key, updater) => {
  if (!key) return;
  const existing = index.get(key) || {
    key,
    name: "",
    inciName: "",
    casNumbers: [],
    synonyms: new Set(),
    flags: [],
    sources: [],
  };
  const next = updater(existing);
  index.set(key, next);
};

const mergeCas = (list, incoming) => {
  const seen = new Set(list.map(normalizeCas));
  incoming.forEach((c) => {
    const n = normalizeCas(c);
    if (n && !seen.has(n)) {
      seen.add(n);
      list.push(n);
    }
  });
  return list;
};

const mergeSynonyms = (set, incoming) => {
  (incoming || []).forEach((s) => {
    const n = String(s || "").trim();
    if (n) set.add(n);
  });
  return set;
};

// ---------------------------------------------------------------------------
// IFRA seed — 261 fragrance ingredients with Category 4 thresholds.
// ---------------------------------------------------------------------------
const seedFromIfra = (index) => {
  Object.entries(structuredIfraData).forEach(([docName, entry]) => {
    const ingredient = entry?.ingredient || {};
    const casList = splitCasList(ingredient.cas_number);
    const key = casList[0] || normalizeText(ingredient.name);
    if (!key) return;

    const cat4 = entry?.usage_restrictions?.categories?.category_4;
    const hasThreshold = typeof cat4 === "number" && cat4 > 0;

    upsert(index, key, (record) => {
      record.name = record.name || ingredient.name || "";
      record.casNumbers = mergeCas(record.casNumbers, casList);
      mergeSynonyms(record.synonyms, ingredient.synonyms);
      mergeSynonyms(record.synonyms, ingredient.commercial_names);

      record.flags.push({
        code: "ifra_standard",
        label: hasThreshold ? `IFRA Cat 4 ${cat4}%` : "IFRA standard",
        severity: hasThreshold ? "restricted" : "listed",
        detail: hasThreshold
          ? `IFRA Category 4 threshold is ${cat4}% for ${ingredient.name}.`
          : `Listed in the IFRA standards library (no numeric Cat 4 limit).`,
      });
      record.sources.push({
        label: `IFRA · ${docName}`,
        url: "https://ifrafragrance.org/standards-library",
        kind: "ifra",
      });
      record.ifra = {
        document: docName,
        category4: cat4,
        amendment: entry.amendment,
      };
      return record;
    });
  });
};

// ---------------------------------------------------------------------------
// EU Annex II/III rules — prohibited and restricted substances.
// ---------------------------------------------------------------------------
const seedFromEuRules = (index, regulatoryRules = []) => {
  regulatoryRules
    .filter((rule) => rule.jurisdiction === "EU" && ["II", "III"].includes(rule.annex))
    .forEach((rule) => {
      const casList = splitCasList(rule.casNumbers);
      const key = casList[0] || normalizeText(rule.substanceName || rule.inciName);
      if (!key) return;

      const isProhibited = rule.ruleType === "prohibited";
      const hasLimit = Number.isFinite(Number(rule.maxPercentage)) && Number(rule.maxPercentage) > 0;

      upsert(index, key, (record) => {
        record.name = record.name || rule.inciName || rule.substanceName || "";
        record.inciName = record.inciName || rule.inciName || "";
        record.casNumbers = mergeCas(record.casNumbers, casList);
        mergeSynonyms(record.synonyms, rule.synonyms);
        mergeSynonyms(record.synonyms, [rule.substanceName, rule.inciName]);

        record.flags.push({
          code: isProhibited ? "eu_annex_ii_prohibited" : "eu_annex_iii_restricted",
          label: isProhibited
            ? `EU Annex II §${rule.referenceNumber} (prohibited)`
            : hasLimit
              ? `EU Annex III §${rule.referenceNumber} ≤${rule.maxPercentage}%`
              : `EU Annex III §${rule.referenceNumber}`,
          severity: isProhibited ? "prohibited" : hasLimit ? "restricted" : "listed",
          detail: isProhibited
            ? `${rule.substanceName || "Substance"} is prohibited in cosmetic products under EU Annex II entry ${rule.referenceNumber}.`
            : hasLimit
              ? `${rule.substanceName || "Substance"} is restricted to a maximum of ${rule.maxPercentage}% in the ready-for-use product (EU Annex III entry ${rule.referenceNumber}).`
              : `${rule.substanceName || "Substance"} is listed under EU Annex III entry ${rule.referenceNumber} (disclosure/labeling requirements).`,
        });
        record.sources.push({
          label: `EUR-Lex · Annex ${rule.annex} §${rule.referenceNumber}`,
          url: rule.sourceUrl || "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A02009R1223-20240424",
          kind: "eurlex",
        });
        record.euAnnex = record.euAnnex || [];
        record.euAnnex.push({
          annex: rule.annex,
          referenceNumber: rule.referenceNumber,
          ruleType: rule.ruleType,
          maxPercentage: rule.maxPercentage,
          sourceUrl: rule.sourceUrl,
        });
        return record;
      });
    });
};

// ---------------------------------------------------------------------------
// MoCRA PFAS list
// ---------------------------------------------------------------------------
const seedFromMocraPfas = (index) => {
  (mocraReference.pfasSubstances?.knownCas || []).forEach((entry) => {
    const cas = normalizeCas(entry.cas);
    if (!cas) return;
    upsert(index, cas, (record) => {
      record.name = record.name || entry.name || "";
      record.casNumbers = mergeCas(record.casNumbers, [cas]);
      record.flags.push({
        code: "mocra_pfas",
        label: `PFAS (${entry.category || "reportable"})`,
        severity: "reporting",
        detail: `${entry.name} is a per-/polyfluoroalkyl substance tracked under FDA MoCRA PFAS reporting (FD&C Act § 608).`,
      });
      record.sources.push({
        label: "FDA · PFAS in Cosmetics",
        url: mocraReference.fdaSources?.pfas?.url || "https://www.fda.gov/cosmetics/cosmetic-ingredients/and-polyfluoroalkyl-substances-pfas-cosmetics",
        kind: "fda_pfas",
      });
      record.sources.push({
        label: "EPA CompTox · PFASSTRUCTV5",
        url: mocraReference.pfasSubstances?.sourceUrl || "https://comptox.epa.gov/dashboard/chemical-lists/PFASSTRUCTV5",
        kind: "epa_comptox",
      });
      return record;
    });
  });
};

// ---------------------------------------------------------------------------
// MoCRA talc list
// ---------------------------------------------------------------------------
const seedFromMocraTalc = (index) => {
  (mocraReference.talcSubstances?.knownCas || []).forEach((entry) => {
    const cas = normalizeCas(entry.cas);
    if (!cas) return;
    upsert(index, cas, (record) => {
      record.name = record.name || entry.name || "";
      record.casNumbers = mergeCas(record.casNumbers, [cas]);
      record.flags.push({
        code: "mocra_talc",
        label: "MoCRA talc testing",
        severity: "testing",
        detail: `${entry.name} is subject to FDA proposed rule 21 CFR § 700.30 requiring standardised asbestos testing in talc-containing products.`,
      });
      record.sources.push({
        label: "FDA · Talc",
        url: mocraReference.talcSubstances?.sourceUrl || "https://www.fda.gov/cosmetics/cosmetic-ingredients/talc",
        kind: "fda_talc",
      });
      return record;
    });
  });
};

// ---------------------------------------------------------------------------
// EU Annex III fragrance allergens (passed in from compliance.js euAllergens).
// ---------------------------------------------------------------------------
export const seedAllergens = (index, allergens = []) => {
  allergens.forEach((allergen) => {
    const cas = normalizeCas(allergen.cas);
    const key = cas || normalizeText(allergen.inciName || allergen.name);
    if (!key) return;
    upsert(index, key, (record) => {
      record.name = record.name || allergen.inciName || allergen.name || "";
      record.inciName = record.inciName || allergen.inciName || "";
      if (cas) record.casNumbers = mergeCas(record.casNumbers, [cas]);
      mergeSynonyms(record.synonyms, [allergen.name, allergen.inciName]);
      record.flags.push({
        code: "eu_allergen",
        label: "EU fragrance allergen",
        severity: "labeling",
        detail: `${allergen.inciName} is a fragrance allergen under EU Annex III; disclosure required above 0.001% (leave-on) / 0.01% (rinse-off).`,
      });
      record.sources.push({
        label: allergen.source || "Reg. (EC) 1223/2009, Annex III",
        url: allergen.sourceUrl || "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A02009R1223-20240424",
        kind: "eurlex_allergen",
      });
      return record;
    });
  });
};

// ---------------------------------------------------------------------------
// Finalise / sort a record.
// ---------------------------------------------------------------------------
const finaliseRecord = (record) => {
  const synonyms = [...record.synonyms].slice(0, 6);
  // Dedup flags by code (keep first)
  const seenFlagCodes = new Set();
  const flags = [];
  for (const flag of record.flags) {
    if (!seenFlagCodes.has(flag.code)) {
      seenFlagCodes.add(flag.code);
      flags.push(flag);
    }
  }
  // Dedup sources by url
  const seenSourceUrls = new Set();
  const sources = [];
  for (const source of record.sources) {
    if (!source.url || !seenSourceUrls.has(source.url)) {
      if (source.url) seenSourceUrls.add(source.url);
      sources.push(source);
    }
  }

  // Derive a single "trust level" chip per record
  const trustLevel = flags.some((f) => f.severity === "prohibited")
    ? "Prohibited"
    : flags.some((f) => f.severity === "restricted")
      ? "Restricted"
      : flags.some((f) => f.severity === "labeling")
        ? "Labeling"
        : flags.some((f) => f.severity === "reporting" || f.severity === "testing")
          ? "Reporting"
          : "Listed";

  return {
    id: record.key,
    name: record.name,
    inciName: record.inciName || record.name,
    casNumber: record.casNumbers[0] || "",
    casNumbers: record.casNumbers,
    synonyms,
    flags,
    sources,
    trustLevel,
    // Legacy fields for the existing DataTable in the UI
    canonicalName: record.name,
    currentInciName: record.inciName || record.name,
    ecNumber: "",
    sourceType: sources[0]?.kind || "",
    ifra: record.ifra,
    euAnnex: record.euAnnex || [],
  };
};

// ---------------------------------------------------------------------------
// Public: build the unified index from the platform state.
// ---------------------------------------------------------------------------
export const buildIngredientExplorer = ({
  regulatoryRules = [],
  regulatoryIngredients = [],
  euAllergens = [],
} = {}) => {
  const index = new Map();

  seedFromIfra(index);
  seedFromEuRules(index, regulatoryRules);
  seedFromMocraPfas(index);
  seedFromMocraTalc(index);
  seedAllergens(index, euAllergens);

  // Also absorb any extra regulatoryIngredients rows the state may have
  (regulatoryIngredients || []).forEach((entry) => {
    const casList = splitCasList(entry.casNumber);
    const key = casList[0] || normalizeText(entry.canonicalName || entry.currentInciName);
    if (!key) return;
    upsert(index, key, (record) => {
      record.name = record.name || entry.canonicalName || entry.currentInciName || "";
      record.inciName = record.inciName || entry.currentInciName || "";
      record.casNumbers = mergeCas(record.casNumbers, casList);
      mergeSynonyms(record.synonyms, entry.relatedNames);
      record.sources.push({
        label: entry.source || "Regulatory registry",
        url: entry.sourceUrl || "",
        kind: entry.sourceType || "registry",
      });
      return record;
    });
  });

  return Array.from(index.values())
    .map(finaliseRecord)
    .sort((a, b) => a.name.localeCompare(b.name));
};

// Lookup by CAS or name — used for detail drill-down.
export const findIngredient = (explorer, needle) => {
  const n = normalizeText(needle);
  const c = normalizeCas(needle);
  return explorer.find((row) => {
    if (c && row.casNumbers.includes(c)) return true;
    const hay = [row.name, row.inciName, ...row.casNumbers, ...row.synonyms]
      .map(normalizeText)
      .filter(Boolean);
    return hay.includes(n);
  });
};
